import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { Basket } from '@df/session/api/basket/basket';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NModalLayoutRight } from '../../common/modal/layout-right/modal-layout-right.component';
import { MinibasketSubmitComponent } from './submit/minibasket-submit.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { BasketViewItemComponent } from '../view/item/basket-view-item.component';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfEventService } from '@df/core/service/df-event';
import { BasketViewTotalsComponent } from '../view/totals/basket-view-totals.component';
import { MinibasketInitiatorService } from './minibasket-initiator.service';

export namespace NCmsMinibasket {
    export const BLOCK_ID = 'minibasket';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent {
        footerNote: string;
        basketCustomerInfo: string;
    }
}

@DfModal({
    id: NCmsMinibasket.BLOCK_ID,
    name: 'Minibasket',
    position: (sizeName: ISizeName) => (sizeName === 'S' ? 'bottom' : 'right'),
    config: sizeName => ({
        ...NModalLayoutRight.getConfig(sizeName),
        width: NModalLayoutRight.getWidth(sizeName),
        height: sizeName === 'S' ? '85dvh' : '100dvh',
        position:
            sizeName === 'S'
                ? { bottom: '0' }
                : {
                      top: '0',
                      right: '0',
                      bottom: '0'
                  },
        hasBackdrop: true
    }),

    initiators: [MinibasketInitiatorService],
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeId => (['S', 'M'].includes(sizeId) ? ['S', 'M'] : ['L', 'X']),
    prerenderAllowed: false,
    forbiddenLocation: /^\/(cart|checkout|success)$/
})
@ICmsComponent<MinibasketComponent>({
    id: NCmsMinibasket.BLOCK_ID,
    block: {
        url: NCmsMinibasket.BLOCK_ID,
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Minibasket',
    description: 'Minibasket Content',
    fields: [
        {
            key: 'footerNote',
            type: NICmsContentField.EFieldType.text,
            title: 'Footer note'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'basketCustomerInfo',
            title: 'Customer basket information'
        }
    ]
})
@Component({
    selector: 'minibasket',
    templateUrl: './minibasket.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MinibasketSubmitComponent,
        TranslationModule,
        ICmsClientModule,
        BasketViewItemComponent,
        DfUiModule,
        BasketViewTotalsComponent
    ]
})
export class MinibasketComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NCmsMinibasket.IBlockDataContent;

    private modalRef = inject(ModalRef<MinibasketComponent>);

    private readonly event = inject(DfEventService);

    readonly basket = inject(Basket);

    isSalable = false;

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(this.basket.subscribe(() => this.onBasketUpdate()));
        this.onBasketUpdate();
    }

    private onBasketUpdate() {
        this.isSalable = this.basket.isSalable;
        this.detectChanges();

        this.event.dispatch({
            id: 'dfBasketView',
            data: {
                context: 'minibasket'
            }
        });
    }

    protected close() {
        this.modalRef.close();
    }
}
